import { useState, useCallback, useEffect, useMemo } from 'react';
import { getCheckboxOptions } from '../types';

interface ComponentState {
  [componentId: string]: {
    disabled: boolean;
    controlledBy?: string; // ID of the checkbox that controls this component
  };
}

export const useComponentState = (components: any[], values: Record<string, any>) => {
  // Use useMemo to recalculate component states whenever values or components change
  const componentStates = useMemo(() => {
    const newStates: ComponentState = {};

    // Process all components to find checkbox-controlled components
    components.forEach(component => {
      if (component.type === 'checkbox') {
        const checkboxOptions = getCheckboxOptions(component);
        const isChecked = values[component.id] === (checkboxOptions.checkedValue ?? true);

        // Handle components to enable when checked
        if (checkboxOptions.enableComponents) {
          checkboxOptions.enableComponents.forEach(targetId => {
            newStates[targetId] = {
              disabled: !isChecked,
              controlledBy: component.id
            };
          });
        }

        // Handle components to disable when checked
        if (checkboxOptions.disableComponents) {
          checkboxOptions.disableComponents.forEach(targetId => {
            newStates[targetId] = {
              disabled: isChecked,
              controlledBy: component.id
            };
          });
        }
      }
    });

    return newStates;
  }, [components, values]);

  // Get the disabled state for a component
  const isComponentDisabled = useCallback((componentId: string, originalDisabled?: boolean): boolean => {
    const state = componentStates[componentId];
    if (state) {
      return state.disabled;
    }
    return originalDisabled ?? false;
  }, [componentStates]);

  // Get the controlled state info for a component
  const getComponentControlInfo = useCallback((componentId: string) => {
    return componentStates[componentId];
  }, [componentStates]);

  return {
    isComponentDisabled,
    getComponentControlInfo,
    componentStates
  };
}; 